import React, {useState, useEffect} from 'react';
import SwitchMUI from '../../../shared/SwitchMUI';
import Head from '../../Components/Profile/Head';
import ButtonMUI from '../../../shared/ButtonMUI';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {notifySuccess} from '../../../helpers/notifySnack';
import {
  getProfileMe,
  patchProfileNotification,
  postProfileNotificationRead
} from '../../../redux/actions/profile/profileActions';

import './UserNotification.scss';
import LoadPanel from '../../Components/LoadPanel';
import {getProjects} from '../../../redux/actions/projects/projectsActions';
import {hasPermission} from "../../../helpers/functions";
import {PERMISSIONS} from "../../../helpers/constants";

const UserNotification = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [buttonLoad, setButtonLoad] = useState(false);
  const [buttonLoadRead, setButtonLoadRead] = useState(false);

  const {profileMe, loading} = useSelector((state) => state.profile);

  const permissionEstimate = hasPermission(PERMISSIONS.ESTIMATE_LIST, profileMe?.permissions ?? []);

  const [settings, setSettings] = useState({
    photoFeedback: true,
    videoFeedback: true,
    blueprint: true,
    documents: true,
    estimate: true,
    membersEdited: true,
    messages: true
  });

  const [initialSettings, setInitialSettings] = useState(settings);

  const notification = [
    {
      title: t('profileNotification.notifications.photoReportTitle'),
      descriptions: t('profileNotification.notifications.photoReport'),
      key: 'photoFeedback'
    },
    {
      title: t('profileNotification.notifications.videoReportTitle'),
      descriptions: t('profileNotification.notifications.videoReport'),
      key: 'videoFeedback'
    },
    {
      title: t('profileNotification.notifications.drawingsTitle'),
      descriptions: t('profileNotification.notifications.drawings'),
      key: 'blueprint'
    },
    {
      title: t('profileNotification.notifications.documentsTitle'),
      descriptions: t('profileNotification.notifications.documents'),
      key: 'documents'
    },
    ...(permissionEstimate
      ? [
          {
            title: t('profileNotification.notifications.estimateTitle'),
            descriptions: t('profileNotification.notifications.estimate'),
            key: 'estimate'
          }
        ]
      : []),
    {
      title: t('profileNotification.notifications.participantChangesTitle'),
      descriptions: t('profileNotification.notifications.participantChanges'),
      key: 'membersEdited'
    } ,
    {
      title: t('profileNotification.notifications.chat'),
      descriptions: t('profileNotification.notifications.chatMessages'),
      key: 'messages'
    }
  ];

  useEffect(() => {
    if (profileMe && profileMe.notificationSettings) {
      setSettings(profileMe.notificationSettings);
      setInitialSettings(profileMe.notificationSettings);
    }
  }, [profileMe]);

  const handleSwitchChange = (key) => (event) => {
    setSettings({
      ...settings,
      [key]: event.target.checked
    });
  };

  const handleNotificationRead = async () => {
    setButtonLoadRead(true);

    dispatch(postProfileNotificationRead()).then(() => {
      dispatch(getProjects());
      notifySuccess(t('all.successfully'));
      setButtonLoadRead(false);
    });
  };

  const handleSaveSettings = async () => {
    setButtonLoad(true);
    try {
      const response = await dispatch(patchProfileNotification(settings));
      if (response.payload?.status === 200) {
        dispatch(getProfileMe());
        setInitialSettings(settings);
        notifySuccess(t('all.successfully'));
      }
    } finally {
      setButtonLoad(false);
    }
  };

  if (loading) {
    return (
      <div className={'companies-page-loader'}>
        <LoadPanel noBg={true} circleSize={50} staticPos={true} />
      </div>
    );
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

  return (
    <div className='user-notification'>
      <Head
        title={t('profileNotification.notificationHead')}
        button={t('profileNotification.notificationHeadBtn')}
        buttonLoadRead={buttonLoadRead}
        handleNotificationRead={handleNotificationRead}
      />

      <div className='user-notification__content'>
        <div className='user-notification__title'>{t('profileNotification.notificationSettings')}</div>

        <div className='user-notification__list'>
          {notification.map(({title, key, descriptions}, idx) => (
            <div key={idx} className='switch'>
              <SwitchMUI color='primary' checked={settings[key]} onChange={handleSwitchChange(key)} />
              <div className='switch__content'>
                <div className='switch__title'>{title}</div>
                <div className='switch__descriptions'>{descriptions}</div>
              </div>
            </div>
          ))}
        </div>

        <ButtonMUI onClick={handleSaveSettings} loading={buttonLoad} disabled={!hasChanges}>
          {t('profileNotification.saveButton')}
        </ButtonMUI>
      </div>
    </div>
  );
};

export default UserNotification;
